import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  type NamePopularityDataSerialized,
  type NamePopularitySerializedResponse,
  NamePopularitySerializer,
  nameInfoParse as useNameInfoParse,
  useFluffer,
} from '@ltvco/refresh-lib/hooks';
import {
  Box,
  Card,
  Divider,
  Link,
  Stack,
  Text,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useSession } from '@ltvco/refresh-lib/v1';
import {
  NamePopularityGraph,
  type NamePopularityProps,
  PlainNameCard,
} from '@ltvco/refresh-lib/v2';
import { LocalLibraryRounded } from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';

import { constants } from 'appConstants';
import { useQueryParams } from 'utils/useQueryParams';

import { LLMNameData } from './Components/LLMNameData';
import { SearchNameForm } from './Components/SearchNameForm';

export interface PopularityGraphProps {
  /** Name Popularity values list associated to the specific Gender */
  data: NamePopularityProps[] | null;
  /** List of categories used on the graphs, as the xAxis or yAxis, mostly used to reference data points categories in a graph */
  categories: string[];
}

function formatGraphData(data: NamePopularityDataSerialized[], gender: string) {
  const labels =
    data?.map((name: NamePopularityDataSerialized) => name.year.toString()) ||
    [];

  const chartData = {
    data: [
      {
        name: gender,
        data: data?.map((name: NamePopularityDataSerialized) => ({
          date: name.year.toString(),
          value: name.rank,
          score: name.rank,
        })),
      },
    ],
    categories: labels,
  };

  return chartData;
}

export const NameHistorySearch = () => {
  const { trackEventGA4 } = useContext(AppConfig);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    session: { account },
  } = useSession();

  const { first_name: userFirstName, last_name: userLastName } =
    account?.account.user_info || {};

  const queryParams = useQueryParams();
  const urlFirstName = queryParams.get('fname') || '';
  const urlLastName = queryParams.get('ln') || '';

  const [firstName, setFirstName] = useState(
    urlFirstName || userFirstName || ''
  );
  const [lastName, setLastName] = useState(urlLastName || userLastName || '');
  const [ancestryPopularityData, setAncestryPopularityData] =
    useState<NamePopularitySerializedResponse>({ male: null, female: null });
  const [graphData, setGraphData] = useState<PopularityGraphProps>({
    data: null,
    categories: [],
  });

  useEffect(() => {
    trackEventGA4({
      event_name: 'page_view',
      type: 'viewed_name_history',
      section_name: 'name_history_page',
    });
  }, []);

  // Both namePopularity and nameInfoParse are hooks despite being improperly
  // labeled for historical reasons, dont make the mistake of thinking they arent
  // Hooks must always be called unconditionally - cannot use conditional hook calls
  const { namePopularity } = useFluffer();
  const namePopularityResult = namePopularity(firstName || '');
  const nameResult = useNameInfoParse(firstName || '', lastName || '') || [];

  if (namePopularityResult?.isSuccess && namePopularityResult?.data) {
    const namePopularityData = NamePopularitySerializer(
      namePopularityResult.data
    );

    let defaultData: NamePopularityDataSerialized[];

    if (namePopularityData.male && namePopularityData.male.length > 0) {
      defaultData = namePopularityData.male;
    } else {
      defaultData = [];
    }

    if (
      ancestryPopularityData.male === null &&
      ancestryPopularityData.female === null
    ) {
      setAncestryPopularityData(namePopularityData);
    }

    if (graphData.data === null) {
      setGraphData(formatGraphData(defaultData, 'Male'));
    }
  }

  function clickBehindTheNameLink() {
    trackEventGA4({
      event_name: 'behindthename link click',
      type: 'person report interaction',
    });
  }

  function loadMaleData() {
    setGraphData(formatGraphData(ancestryPopularityData.male || [], 'Male'));
  }

  const loadFemaleData = () => {
    setGraphData(
      formatGraphData(ancestryPopularityData.female || [], 'Female')
    );
  };

  // Handle form submission
  const handleNameSearch = (firstName: string, lastName: string) => {
    setFirstName(firstName);
    setLastName(lastName);
    // Reset the graph data to trigger new search
    setAncestryPopularityData({ male: null, female: null });
    setGraphData({ data: null, categories: [] });

    trackEventGA4({
      event_name: 'navigation_click',
      type: 'search_name_history',
      interacted_from: 'search_button',
      section_name: 'name_history_page',
    });
  };

  // Helper function to render name card with attribution
  const renderNameCard = (nameInfo: any, cardStyle?: any) => (
    <Card
      sx={{
        padding: 2.5,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        ...cardStyle,
      }}
      variant="outlined"
    >
      <PlainNameCard nameInfo={nameInfo} key={nameInfo.id} />
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <Text fontSize={14}>Info provided by</Text>
        <Link
          href="https://www.behindthename.com/"
          onClick={clickBehindTheNameLink}
          fontSize={14}
          underline="always"
          target="_blank"
          rel="noopener"
          color="text.primary"
        >
          behindthename.com
        </Link>
      </Stack>
    </Card>
  );

  // Helper function to render graph card
  const renderGraphCard = (cardStyle?: any) => (
    <Card
      sx={{
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        ...cardStyle,
      }}
      variant="outlined"
    >
      <NamePopularityGraph
        firstName={firstName}
        data={graphData.data}
        category={{
          name: graphData?.data?.[0]?.name ?? '',
          show:
            (graphData?.data && graphData?.data?.[0]?.data.length > 0) ?? false,
        }}
        categories={graphData.categories}
        onMaleBtnClick={loadMaleData}
        onFemaleBtnClick={loadFemaleData}
      />
    </Card>
  );

  const hasFirstName = nameResult.some((result) => !result.isSurname);
  const shouldUseSingleRowLayout =
    nameResult.length === 1 && nameResult[0].isSurname === false && graphData;

  return (
    <>
      <Text variant="h2" marginBottom={3}>
        <LocalLibraryRounded /> Name History Search
      </Text>
      <Card
        sx={{
          padding: 2.5,
          marginBottom: 2.5,
          backgroundImage: `url(${constants.images.imgNameHistorySearch})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
        variant="outlined"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            alignItems={{ xs: 'center', md: 'center' }}
            sx={{ alignSelf: { xs: 'center', md: 'stretch' } }}
          >
            <Box
              sx={{
                width: 119,
                height: 119,
                minWidth: 119,
                minHeight: 119,
                flexShrink: 0,
                backgroundColor: 'background.paper',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow:
                  '0px 1px 10px 0px rgba(0, 82, 255, 0.12), 0px 4px 4px 0px rgba(0, 82, 255, 0.12)',
              }}
            >
              <LocalLibraryRounded sx={{ fontSize: 32 }} />
            </Box>
            <Stack
              direction={{ xs: 'row', md: 'column' }}
              spacing={{ xs: 4, md: 3 }}
              sx={{
                minWidth: '266px',
                alignItems: { xs: 'flex-start', md: 'stretch' },
              }}
            >
              <Stack spacing={0.5}>
                <Text variant="caption" color="text.secondary">
                  FIRST NAME{' '}
                </Text>
                <Text variant="h3">{firstName || '-'}</Text>
              </Stack>
              <Stack spacing={0.5}>
                <Text variant="caption" color="text.secondary">
                  LAST NAME
                </Text>
                <Text variant="h3">{lastName || '-'}</Text>
              </Stack>
            </Stack>
          </Stack>
          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem
          />
          <SearchNameForm
            currentFirstName={firstName || undefined}
            currentLastName={lastName || undefined}
            onSubmit={handleNameSearch}
          />
        </Stack>
      </Card>
      {/* Begin Data from Behind the Name */}
      {shouldUseSingleRowLayout ? (
        // Single row layout: name card and graph side by side on desktop, stacked on mobile
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={0.5}
          alignItems="stretch"
          sx={{ marginBottom: 2.5 }}
        >
          {renderNameCard(nameResult[0])}
          {renderGraphCard()}
        </Stack>
      ) : (
        // Default layout: name cards in one row on desktop, stacked on mobile; graph below
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.5}
            alignItems="stretch"
            sx={{ marginBottom: 2.5 }}
          >
            {nameResult.map((nameInfo) => renderNameCard(nameInfo))}
          </Stack>
          {graphData && hasFirstName && renderGraphCard()}
        </>
      )}
      {/* Begin Data from LLM*/}
      <LLMNameData
        firstName={firstName}
        lastName={lastName}
        onNameSearch={handleNameSearch}
      />
    </>
  );
};
